import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { getCompanySettings } from '@/utils/companySettings';
import { X, Menu, Home, Briefcase, Layers, Timer, Play, Pause, Shield,
  ChevronDown, PanelLeft, PanelLeftClose, Sun, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import HelpIcon from '@/components/HelpIcon';
import WhosOnline from '@/components/WhosOnline';
import NotificationsPanel from '@/components/NotificationsPanel';
import GlobalActionItems from '@/components/GlobalActionItems';
import { BrandLockup } from '@/components/brand/Brand';
import { SIDEBAR_SECTIONS } from '@/data/navShellManifest';
import AdminToolsSquare from '@/components/layout/AdminToolsSquare';
import { renderNavIcon } from '@/components/layout/navIcon';
import { RailBox } from '@/components/layout/RailBox';
import { railGlyphMode, glyphFor, findCollisions, RAIL_GLYPH_FIELD } from '@/utils/railGlyph';
import { useRole } from '@/contexts/RoleContext';
import { useFocusSession } from '@/contexts/FocusSessionContext';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useMastheadShrink } from '@/hooks/useMastheadShrink';
import { getCurrentUserId } from '@/utils/currentUser';
import { cacheBranding } from '@/utils/skin';
import { APP_VERSION } from '@/version';
import '@/styles/km-shell.css';
import '@/styles/dock.css';
// DP70 Part 1b — the frame's own stylesheet, loaded WITH the frame.
// It was imported only by pages/UserDashboard.js (My Desk). Routes are lazy, so
// on a direct visit to All Projects or the Work Table home the whole km-frame
// layer was absent: the rail's Time Tracker label and clock fell back to
// --km-ink on the dark rail and measured 1.03:1. My Desk was unaffected because
// its own import had already put the sheet in the document.
import '@/styles/km-frame.css';
// NAV-1 — the rail's hover flyouts.
import RailFlyout, { useRailFlyout } from '@/components/nav/RailFlyout';

/**
 * APP SHELL — DP56, shell pass B.
 *
 * The rail, the rail background, the top strip and the content region used to
 * live inside pages/UserDashboard.js and nowhere else, which is why no other
 * page could have them. They are lifted here VERBATIM: same markup, same
 * classes, same testids, same order. Nothing was redesigned.
 *
 * What changed, and only this:
 *   - the shell is pinned to the viewport (height: 100vh, overflow hidden) and
 *     ONLY .km-shell__content scrolls. Before, .km-shell was min-h-screen and
 *     grew, so the window scrolled and the rail scrolled away.
 *   - rail destinations with no route (Tasks, Calendar, Time Tracking, Notes)
 *     are My Desk tabs. On My Desk they still switch the tab; from another page
 *     they navigate to /dashboard?tab=<id>, which UserDashboard already reads.
 *   - the collapse state persists (sessionStorage), so it survives the move
 *     between pages. It did not persist before — it was plain useState(false).
 *   - the top strip's right-hand group is a slot, so each page supplies its own
 *     actions instead of the shell inventing a set.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// SH-1c — the rail is always a dark surface, so it always takes the dark-background logo when one is set.
const railLogoSrc = (cs) => {
  const u = (cs && (cs.logo_light_url || cs.logo_url)) || '';
  if (!u) return '';
  return (u.startsWith('data:') || u.startsWith('http')) ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;
};

const RAIL_COLLAPSED_KEY = 'keyMadeRailCollapsed';
const SECTIONS_KEY = 'keyMadeDeskSections';

const BREADCRUMBS = {
  mydesk: ['My Desk'],
  projects: ['My Desk', 'All Projects'],
  worktable: ['My Desk', 'Work Table'],
  admin: ['My Desk', 'Admin'],
};
const CRUMB_ROUTES = { 'My Desk': '/dashboard', 'All Projects': '/projects', 'Work Table': '/work-table', 'Admin': '/admin' };

export const AppShell = ({
  page,
  masthead = null,   // DP94 Part 1 — the full-width band, above the panel
  activeTab = null,
  onTabChange = null,
  breadcrumbTail = null,
  topStripActions = null,
  belowTopStrip = null,
  shellTestId = 'app-shell',
  companySettings: companySettingsProp = undefined,
  iconAssignments: iconAssignmentsProp = undefined,
  globalIconSettings: globalIconSettingsProp = undefined,
  projects: projectsProp = undefined,
  needsCompletionCount: needsCompletionCountProp = undefined,
  trackerProjectId: trackerProjectIdProp = undefined,
  onTrackerProjectIdChange = null,
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { role, isAdmin, isFinancial } = useRole();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const { activeFocusSession, focusSeconds, startFocusSession, stopFocusSession, formatTimerDisplay } = useFocusSession();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => sessionStorage.getItem(RAIL_COLLAPSED_KEY) === 'true'
  );
  // DP60 Part 0a — the four groups DEFAULT CLOSED. This behaviour existed
  // before the DP56 shell lift (UserDashboard.js:542 at commit 0fc3f9f6) and my
  // lift dropped it: I wrote `return {}`, which reads as all four OPEN. The
  // default below is the pre-lift one, value for value. Session-only, same
  // sessionStorage key as before, so a group opened stays open across pages and
  // a new session starts closed again.
  const [collapsedSections, setCollapsedSections] = useState(() => {
    // The shape is validated, not trusted: the try/catch only ever guarded
    // JSON.parse, so a stored `"x"` or `[1,2]` would have been spread in as-is
    // and read as all four OPEN — the exact failure this default exists to
    // prevent. Anything that is not a plain object falls through to closed.
    // Found by the independent test pass, iteration_494.
    try {
      const saved = sessionStorage.getItem(SECTIONS_KEY);
      const parsed = saved ? JSON.parse(saved) : null;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
    } catch (_e) { /* fall through to the closed default */ }
    return { connections: true, workspace: true, resources: true, operations: true, admin: true };
  });

  useEffect(() => {
    sessionStorage.setItem(RAIL_COLLAPSED_KEY, sidebarCollapsed.toString());
    /* DP106 Part 5a — the rail's width, published so an overlay can stop at its
       edge instead of covering the navigation. The rail itself is 72px collapsed
       and 192px open, and it is not rendered below md. */
    const w = window.innerWidth < 768 ? '0px' : (sidebarCollapsed ? '72px' : '192px');
    document.documentElement.style.setProperty('--km-rail-w', w);
  }, [sidebarCollapsed]);

  // The shell is exactly the viewport, so the WINDOW must not scroll behind it.
  // Scoped to a body class so every page that is NOT in the shell keeps its own
  // window scrolling untouched.
  useEffect(() => {
    document.body.classList.add('km-shell-locked');
    return () => document.body.classList.remove('km-shell-locked');
  }, []);

  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => {
      const updated = { ...prev, [sectionId]: !prev[sectionId] };
      sessionStorage.setItem(SECTIONS_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  // Shell-owned copies, fetched ONLY when the page did not already have them.
  const [shellCompany, setShellCompany] = useState(null);
  const [shellIcons, setShellIcons] = useState({ assignments: {}, global: {} });
  const [shellProjects, setShellProjects] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const needsOwnData = companySettingsProp === undefined;

  useEffect(() => {
    if (!needsOwnData) return;
    let cancelled = false;
    (async () => {
      const [companyRes, iconRes, projectsRes] = await Promise.all([
        getCompanySettings().then((d) => ({ data: d })).catch(() => ({ data: null })),
        axios.get(`${API}/icon-library`).catch(() => ({ data: { assignments: {} } })),
        axios.get(`${API}/projects`).catch(() => ({ data: [] })),
      ]);
      if (cancelled) return;
      setShellCompany(companyRes.data || null);
      setShellIcons({
        assignments: iconRes.data?.assignments || {},
        global: iconRes.data?.global_settings || {},
      });
      const raw = Array.isArray(projectsRes.data) ? projectsRes.data : (projectsRes.data?.projects || []);
      setShellProjects([...raw].sort((a, b) => Number(!!a.is_test_fixture) - Number(!!b.is_test_fixture)));
    })();
    return () => { cancelled = true; };
  }, [needsOwnData]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/users/${getCurrentUserId()}/profile`);
        if (!cancelled) setIsSuperAdmin(res.data?.is_super_admin || false);
      } catch (_e) { /* non-fatal — the group simply stays hidden */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const companySettings = companySettingsProp !== undefined ? companySettingsProp : shellCompany;
  // DP92 Part A — the firm's skin and its five surfaces come off the tenant
  // record. Cached so the FIRST paint of the next page load is already the
  // firm's own look; applySkin re-resolves through the one named resolver.
  useEffect(() => {
    if (companySettings) cacheBranding(companySettings);
  }, [companySettings]);
  const iconAssignments = iconAssignmentsProp !== undefined ? iconAssignmentsProp : shellIcons.assignments;
  const globalIconSettings = globalIconSettingsProp !== undefined ? globalIconSettingsProp : shellIcons.global;
  const projects = projectsProp !== undefined ? projectsProp : shellProjects;
  const needsCompletionCount = needsCompletionCountProp !== undefined ? needsCompletionCountProp : 0;

  // DP60 Part 0e — LETTERS or ICONS is a tenant setting, READ not built.
  // Field: company_settings.rail_glyph_mode ('letters' | 'icons'). Missing,
  // unreadable or unrecognised => letters. DP61 owns the Branding control.
  const glyphMode = railGlyphMode(companySettings);

  // DP60 Part 0d — two letters ALWAYS. Where two visible boxes would collide,
  // it is REPORTED here (and on data-rail-glyph-collisions) rather than one of
  // them silently growing to three letters.
  // Part 0b Part 1a/1d — the seven, and only the seven, carry letters, so this
  // is the whole population that could collide. It reads 0 by construction and
  // is checked rather than assumed.
  const glyphEntries = [
    { id: 'mydesk', label: 'My Desk' },
    { id: 'projects', label: 'Projects' },
    { id: 'worktable', label: 'Work Table' },
    ...SIDEBAR_SECTIONS.map(sec => ({ id: sec.id, label: sec.label })),
  ];
  const glyphCollisions = findCollisions(glyphEntries);

  const [ownTrackerProjectId, setOwnTrackerProjectId] = useState('none');
  const trackerProjectId = trackerProjectIdProp !== undefined ? trackerProjectIdProp : ownTrackerProjectId;
  const onTrackerProjectId = onTrackerProjectIdChange || setOwnTrackerProjectId;

  const appSection = page;

  // DP60 Part 0f — the collapsed tracker must show time PASSING in 40px. mm:ss
  // at 10px monospace measures ~30px and fits; once an hour is up it becomes
  // h:mm:ss at 8px, which is the widest it ever gets.
  const collapsedClock = (() => {
    const t = focusSeconds || 0;
    const h = Math.floor(t / 3600);
    const m = Math.floor((t % 3600) / 60);
    const sec = t % 60;
    const pad = (n) => String(n).padStart(2, '0');
    // Under an hour: mm:ss. One hour and over: h:mm on ONE line — hours and
    // minutes, not minutes and seconds. Two lines broke the even spacing; type
    // below the 9px floor is never the answer (tokens section 5).
    return h > 0 ? `${h}:${pad(m)}` : `${pad(m)}:${pad(sec)}`;
  })();
  const isWorkTable = location.pathname.startsWith('/work-table');
  // NAV-1 — the two rail items that open a quick list on a resting pointer.
  const projectsFlyout = useRailFlyout('projects');
  const worktableFlyout = useRailFlyout('worktable');

  // A rail destination with no route is a My Desk tab. On My Desk switch the
  // tab in place; from anywhere else go to My Desk and let it read ?tab=.
  const goToTab = (tabId) => {
    if (page === 'mydesk' && onTabChange) {
      onTabChange(tabId);
    } else {
      navigate(tabId === 'mydesk' ? '/dashboard' : `/dashboard?tab=${tabId}`);
    }
  };

  const crumbs = [...(BREADCRUMBS[page] || ['My Desk']), ...(breadcrumbTail ? [breadcrumbTail] : [])];

  // DP95 Part 1 — the band's height tracks the PANEL's scroll position.
  const { contentRef, mastRef, panelRef, openBand } = useMastheadShrink();

  // DP68 Part 1a — the frame colour is owned by .km-shell in km-shell.css and
  // is always var(--km-rail). No dark-mode branch: that branch was what put a
  // warm tan on three sides of every light-mode page.
  return (
    <div className="km-shell flex" data-testid={shellTestId}>
    <div className="km-shell__frame" data-testid="shell-frame">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
          style={{ touchAction: 'none' }}
        />
      )}
      
      {/* Sidebar Background Column - Hidden on mobile, extends full height */}
      {/* DP92 Part A — `absolute`, not `fixed`. Fixed pinned it to the viewport,
          which put the rail colour over the new outer mat on the left and along
          the top and bottom edges. Inside .km-shell__frame (position: relative)
          it now stops at the frame. */}
      <div 
        className={`km-rail-bg hidden md:block absolute top-0 left-0 bottom-0 z-[var(--km-z-rail)] transition-all duration-300 ${sidebarCollapsed ? 'w-[72px]' : 'w-48'}`}
        data-km-surface="dark"
        style={{ pointerEvents: 'none' }}
      />
      
      {/* Collapsible Sidebar - Hidden on mobile unless menu is open */}
      <aside 
        data-km-surface="dark"
        className={`km-rail flex flex-col transition-all duration-300
          ${mobileMenuOpen ? 'fixed inset-y-0 left-0 z-50 w-64 overflow-y-auto overscroll-contain' : 'hidden'} 
          md:relative md:flex md:overflow-visible ${sidebarCollapsed ? 'md:w-[72px]' : 'md:w-48'}
        `}
        style={{ minHeight: '100%', height: '100%', zIndex: 'var(--km-z-rail)', touchAction: mobileMenuOpen ? 'pan-y' : 'auto' }}
        onTouchMove={(e) => mobileMenuOpen && e.stopPropagation()}
      >
        {/* Mobile Close Button - Only show on mobile when menu is open */}
        <div className={`md:hidden flex items-center justify-between p-3 border-b ${darkMode ? 'border-[#1A1A1A]' : 'border-border'}`}>
          {/* Mobile Menu Header with Logo */}
          <div className="flex items-center gap-2">
            {(companySettings?.logo_light_url || companySettings?.logo_url) ? (
              <img 
                src={railLogoSrc(companySettings)}
                alt={companySettings?.company_name || "Company"} 
                className="h-8 w-auto object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <span className={`font-heading text-sm font-semibold ${darkMode ? 'text-[#E5E2DD]' : 'text-[#70170F]'}`}>
                {companySettings?.company_name || 'KEY-MADE'}
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className={`h-8 w-8 p-0 ${darkMode ? 'text-[#E5E2DD] hover:bg-[#1A1A1A]' : ''}`}
            onClick={() => setMobileMenuOpen(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Company Logo from Admin Settings */}
        <div className={`p-3 border-b ${darkMode ? 'border-[#1A1A1A]' : 'border-border'} ${sidebarCollapsed ? 'flex justify-center' : ''} hidden md:block ${darkMode ? '' : 'bg-white'}`}>
          {(companySettings?.logo_light_url || companySettings?.logo_url) ? (
            // Logo exists - show full-width logo with tagline below
            <div className={`${sidebarCollapsed ? '' : 'flex flex-col items-center'}`}>
              <img 
                src={railLogoSrc(companySettings)}
                alt={companySettings?.company_name || "Company"} 
                className={sidebarCollapsed ? "w-10 h-10 object-contain" : "w-full max-h-16 object-contain"}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              {!sidebarCollapsed && companySettings?.tagline && (
                <p className={`text-[10px] italic mt-1 text-center ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>
                  {companySettings.tagline}
                </p>
              )}
            </div>
          ) : (
            // No logo - show company name with tagline
            <div className={`${sidebarCollapsed ? '' : 'flex flex-col items-center'}`}>
              {sidebarCollapsed ? (
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-[#70170F]' : 'bg-[#70170F]'}`}>
                  <span className="text-white font-bold text-lg">
                    {(companySettings?.company_name || 'KM').substring(0, 2).toUpperCase()}
                  </span>
                </div>
              ) : (
                <>
                  <h1 className={`font-heading text-base font-semibold text-center ${darkMode ? 'text-[#E5E2DD]' : 'text-[#70170F]'}`}>
                    {companySettings?.company_name || 'KEY-MADE'}
                  </h1>
                  <p className={`text-[10px] italic mt-0.5 text-center ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>
                    {companySettings?.tagline || 'Design that moves you'}
                  </p>
                </>
              )}
            </div>
          )}
        </div>
        
        {/* DP60 Part 0b — the rail has a FIXED UPPER REGION: the three primary
            destinations, then the time tracker. Neither scrolls. Only the four
            groups below scroll. DP59 pinned the tracker at the FOOT of the rail
            above the Admin square, which is the same problem in a different
            place; it is back below the destinations and above the groups. */}
        <div
          className="km-rail__top shrink-0 px-2 pt-2"
          data-testid="rail-fixed-top"
          data-rail-glyph-mode={glyphMode}
          // Part 0b Part 1d — always rendered, so 0 READS as 0 rather than
          // vanishing. `length || undefined` dropped the attribute entirely at
          // zero, which is indistinguishable from "never computed".
          data-rail-glyph-collisions={String(glyphCollisions.length)}
        >
          {sidebarCollapsed ? (
            /* DP60 Part 0d — collapsed: identical lettered boxes, one column. */
            <div className="km-railcol" data-testid="rail-collapsed-top">
              <RailBox id="mydesk" label="My Desk" mode={glyphMode} darkMode={darkMode}
                       icon={renderNavIcon({ id: 'mydesk', icon: Home }, iconAssignments, API, darkMode, false, globalIconSettings)}
                       active={page === 'mydesk' && activeTab === 'mydesk'}
                       onClick={() => { goToTab('mydesk'); setMobileMenuOpen(false); }} />
              <RailBox id="projects" label="Projects" mode={glyphMode} darkMode={darkMode}
                       icon={renderNavIcon({ id: 'projects', icon: Briefcase }, iconAssignments, API, darkMode, false, globalIconSettings)}
                       active={page === 'projects'}
                       onClick={() => { navigate('/projects'); setMobileMenuOpen(false); }} />
              <RailBox id="worktable" label="Work Table" mode={glyphMode} darkMode={darkMode}
                       icon={renderNavIcon({ id: 'worktable', icon: Layers }, iconAssignments, API, darkMode, false, globalIconSettings)}
                       active={isWorkTable}
                       onClick={() => { navigate('/work-table'); setMobileMenuOpen(false); }} />

              {/* Part 0f — the tracker splits the destinations from the groups. */}
              <RailBox
                id="tracker"
                glyph={null}
                label={activeFocusSession ? `Time Tracker — running ${collapsedClock}` : 'Time Tracker'}
                mode={glyphMode}
                darkMode={darkMode}
                testId="rail-box-tracker"
                onClick={() => (activeFocusSession ? stopFocusSession() : startFocusSession(null, '', appSection))}
              >
                {activeFocusSession ? (
                  <span className="km-railbox__clock" data-testid="time-tracker-clock-collapsed">
                    {collapsedClock}
                  </span>
                ) : (
                  <Timer className="w-4 h-4" data-testid="time-tracker-start-collapsed-icon" />
                )}
              </RailBox>
            </div>
          ) : (
          <>
          {/* My Desk — Home button at top */}
          {/* DESIGN PASS 17 — the top three are destinations. The four below
              are groups of contents, and the rail says so. */}
          <div className="km-rail__dest" data-testid="rail-destinations">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => { goToTab('mydesk'); setMobileMenuOpen(false); }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                      page === 'mydesk' && activeTab === 'mydesk' 
                        ? darkMode 
                          ? 'bg-[#70170F]/20 text-[#BC5D41]' 
                          : 'bg-[#BC5D41]/10 text-[#BC5D41]'
                        : darkMode 
                          ? 'text-[#8A8A8A] hover:bg-[#252525] hover:text-[#E5E2DD]'
                          : 'text-muted-foreground hover:bg-gray-50 hover:text-foreground'
                    } ${sidebarCollapsed ? 'justify-center' : ''}`}
                    data-km-active={page === 'mydesk' && activeTab === 'mydesk' ? 'true' : undefined}
                    data-testid="nav-mydesk"
                  >
                    {renderNavIcon({ id: 'mydesk', icon: Home }, iconAssignments, API, darkMode, page === 'mydesk' && activeTab === 'mydesk', globalIconSettings)}
                    {!sidebarCollapsed && (
                      <>
                        <span className="text-sm flex-1 text-left font-medium">My Desk</span>
                        <HelpIcon elementId="nav-my-desk" size="sm" position="right" />
                      </>
                    )}
                  </button>
                </TooltipTrigger>
                {sidebarCollapsed && (
                  <TooltipContent side="right" className={darkMode ? 'bg-[#1A1A1A] text-[#E5E2DD] border-[#2A2A2A]' : ''}>
                    My Desk
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>

            {/* Projects — NAV-1: resting on it opens the quick list. */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    {...projectsFlyout.anchorProps}
                    onClick={() => { navigate('/projects'); setMobileMenuOpen(false); }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                      page === 'projects' 
                        ? darkMode 
                          ? 'bg-[#70170F]/20 text-[#BC5D41]' 
                          : 'bg-[#BC5D41]/10 text-[#BC5D41]'
                        : darkMode 
                          ? 'text-[#8A8A8A] hover:bg-[#252525] hover:text-[#E5E2DD]'
                          : 'text-muted-foreground hover:bg-gray-50 hover:text-foreground'
                    } ${sidebarCollapsed ? 'justify-center' : ''}`}
                    data-km-active={page === 'projects' ? 'true' : undefined}
                    data-testid="nav-projects"
                  >
                    {renderNavIcon({ id: 'projects', icon: Briefcase }, iconAssignments, API, darkMode, page === 'projects', globalIconSettings)}
                    {!sidebarCollapsed && (
                      <>
                        <span className="text-sm flex-1 text-left">Projects</span>
                        <HelpIcon elementId="nav-projects" size="sm" position="right" />
                      </>
                    )}
                  </button>
                </TooltipTrigger>
                {sidebarCollapsed && (
                  <TooltipContent side="right" className={darkMode ? 'bg-[#1A1A1A] text-[#E5E2DD] border-[#2A2A2A]' : ''}>
                    Projects
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>

            {/* Work Table — single destination, not a dropdown */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    {...worktableFlyout.anchorProps}
                    onClick={() => { navigate('/work-table'); setMobileMenuOpen(false); }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                      isWorkTable
                        ? darkMode 
                          ? 'bg-[#70170F]/20 text-[#BC5D41]' 
                          : 'bg-[#BC5D41]/10 text-[#BC5D41]'
                        : darkMode 
                          ? 'text-[#8A8A8A] hover:bg-[#252525] hover:text-[#E5E2DD]'
                          : 'text-muted-foreground hover:bg-gray-50 hover:text-foreground'
                    } ${sidebarCollapsed ? 'justify-center' : ''}`}
                    data-km-active={isWorkTable ? 'true' : undefined}
                    data-testid="nav-worktable"
                  >
                    {renderNavIcon({ id: 'worktable', icon: Layers }, iconAssignments, API, darkMode, isWorkTable, globalIconSettings)}
                    {!sidebarCollapsed && (
                      <>
                        <span className="text-sm flex-1 text-left">Work Table</span>
                        <HelpIcon elementId="nav-work-table" size="sm" position="right" />
                      </>
                    )}
                  </button>
                </TooltipTrigger>
                {sidebarCollapsed && (
                  <TooltipContent side="right" className={darkMode ? 'bg-[#1A1A1A] text-[#E5E2DD] border-[#2A2A2A]' : ''}>
                    Work Table
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* NAV-1 — the quick lists, anchored to their own rail item. */}
          <RailFlyout {...projectsFlyout.panelProps} />
          <RailFlyout {...worktableFlyout.panelProps} />

          {/* Time tracker — fixed, directly below the three destinations. */}
          <div className="km-rail__trackerpin pt-2" data-testid="rail-tracker-region">
            {/* DP36 §3 — one bordered object, not four stacked rows. */}
            <div className="km-rail__tracker" data-testid="time-tracker">
              {/* DP60 Part 0b — the old `sidebarCollapsed ? …` branch was
                  DELETED here. It is unreachable: the collapsed rail renders
                  rail-box-tracker instead, so time-tracker-start-collapsed and
                  time-tracker-stop-collapsed could never appear. Dead code with
                  live-looking test ids is worse than no code.
                  Found by the independent test pass, iteration_494. */}
              {(
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="km-rail__label" data-testid="time-tracker-label">
                      Time Tracker
                    </span>
                    {activeFocusSession && (
                      <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    )}
                  </div>

                  {/* Timer Display */}
                  <div className="km-rail__clock" data-testid="time-tracker-clock">
                    {formatTimerDisplay(focusSeconds)}
                  </div>

                  {/* Project/Admin Selector */}
                  {!activeFocusSession && (
                    <Select
                      value={trackerProjectId}
                      onValueChange={onTrackerProjectId}
                    >
                      <SelectTrigger className={`h-9 text-sm ${darkMode ? 'bg-[#2A2A2A] border-[#2A2A2A]' : ''}`} data-testid="time-tracker-project-select">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin Work</SelectItem>
                        <SelectItem value="none">General</SelectItem>
                        {projects.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {activeFocusSession && (
                    <p className="text-xs text-center text-muted-foreground truncate">
                      {activeFocusSession.project_id === 'admin' ? 'Admin Work' :
                       activeFocusSession.project_id ? projects.find(p => p.id === activeFocusSession.project_id)?.name || 'Project' :
                       'General'}
                    </p>
                  )}

                  {/* Start/Stop Button */}
                  {activeFocusSession ? (
                    <Button onClick={stopFocusSession} variant="outline" className="km-rail__stop w-full h-9" data-testid="time-tracker-stop">
                      <Pause className="w-4 h-4 mr-2" />
                      Stop
                    </Button>
                  ) : (
                    <Button
                      onClick={() => startFocusSession(
                        trackerProjectId === 'admin' || trackerProjectId === 'none' ? null : trackerProjectId,
                        trackerProjectId === 'admin' ? 'Admin Work' : '', appSection
                      )}
                      className="km-rail__start w-full h-9"
                      data-testid="time-tracker-start"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Start
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
          </>
          )}
        </div>

        {/* Main Navigation — the four groups. This is the ONLY part of the rail
            that scrolls. */}
        <nav
          className="flex-1 min-h-0 p-2 pb-4 overflow-y-auto scrollbar-hide km-rail__scroll--fade"
          data-testid="nav-sidebar"
        >
          {sidebarCollapsed ? (
            /* DP60 Part 0d — collapsed groups: same boxes, same spacing. A group
               box toggles its group; its items appear below it as boxes. */
            <div className="km-railcol" data-testid="rail-collapsed-groups">
              {SIDEBAR_SECTIONS.map(section => (
                <React.Fragment key={section.id}>
                  <RailBox id={section.id} label={section.label} mode={glyphMode} darkMode={darkMode}
                           icon={<section.icon className="w-4 h-4" />}
                           active={!collapsedSections[section.id]}
                           testId={`section-${section.id}`}
                           onClick={() => toggleSection(section.id)} />
                  {/* Part 0b Part 1b — destinations INSIDE a group use their
                      ICON, the one the expanded rail shows. mode="icons", not
                      the tenant setting, which governs the seven only. */}
                  {!collapsedSections[section.id] && section.items.map(item => (
                    <RailBox key={item.id} id={item.id} label={item.label} mode="icons" darkMode={darkMode}
                             icon={renderNavIcon(item, iconAssignments, API, darkMode, false, globalIconSettings)}
                             active={!item.path && !item.isLibraryHub && page === 'mydesk' && activeTab === item.id}
                             testId={`nav-${item.id}`}
                             onClick={() => {
                               if (item.isLibraryHub) {
                                 window.dispatchEvent(new CustomEvent('toggle-library-hub'));
                               } else if (item.path) {
                                 navigate(item.path);
                               } else {
                                 goToTab(item.id);
                               }
                               setMobileMenuOpen(false);
                             }} />
                  ))}
                </React.Fragment>
              ))}
              {/* The collapse control is a control, not a destination, so it
                  carries the icon rather than two letters — but it is the SAME
                  box, so the column stays one style. */}
              <RailBox id="collapse" label="Expand Sidebar" mode="icons" darkMode={darkMode}
                       icon={<PanelLeft className="w-4 h-4" />}
                       testId="sidebar-collapse-toggle"
                       onClick={() => setSidebarCollapsed(false)} />
            </div>
          ) : (
          <>
          {/* Collapsible Sections — uniform style, no color, red when active */}
          <div className="km-rail__groups" data-testid="rail-groups">
          {SIDEBAR_SECTIONS.map(section => {
            const SectionIcon = section.icon;
            return (
            <div key={section.id} className="mb-1">
              {/* Section Header */}
              {!sidebarCollapsed ? (
                <button
                  onClick={() => toggleSection(section.id)}
                  className={`w-full text-[10px] font-semibold uppercase tracking-wider mb-0.5 px-2 py-1.5 flex items-center gap-1.5 rounded-md transition-colors ${
                    darkMode 
                      ? 'text-[#8A8A8A] hover:bg-[#252525] hover:text-[#E5E2DD]' 
                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                  }`}
                  data-testid={`section-${section.id}`}
                >
                  <ChevronDown className={`w-3 h-3 transition-transform ${collapsedSections[section.id] ? '-rotate-90' : ''}`} />
                  <SectionIcon className="w-3.5 h-3.5" />
                  <span className="flex-1 text-left">{section.label}</span>
                  {/* Bubble the Contacts needs-completion badge up to the
                      Connections section header so users still see it
                      while the section is collapsed. Click still toggles
                      the section open — the badge is decorative only. */}
                  {section.id === 'connections' && collapsedSections[section.id] && needsCompletionCount > 0 && (
                    <span
                      className="inline-flex items-center justify-center px-1.5 min-w-[16px] h-[16px] rounded-full bg-amber-100 text-amber-800 text-[9px] font-semibold border border-amber-300"
                      title={`${needsCompletionCount} contact${needsCompletionCount === 1 ? '' : 's'} need completing`}
                      data-testid={`section-${section.id}-needs-completion-badge`}
                    >
                      {needsCompletionCount > 99 ? '99+' : needsCompletionCount}
                    </span>
                  )}
                </button>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => toggleSection(section.id)}
                        className={`w-full flex justify-center py-1.5 mb-0.5 rounded-md transition-colors ${
                          darkMode ? 'hover:bg-[#252525]' : 'hover:bg-gray-50'
                        }`}
                        data-testid={`section-${section.id}`}
                      >
                        <SectionIcon className={`w-4 h-4 ${darkMode ? 'text-[#8A8A8A]' : 'text-gray-500'}`} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className={darkMode ? 'bg-[#1A1A1A] text-[#E5E2DD] border-[#2A2A2A]' : ''}>
                      {section.label}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {/* Section Items */}
              {!collapsedSections[section.id] && (
                <div className="space-y-0.5">
                  {section.items.map(item => (
                    <TooltipProvider key={item.id}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => {
                              if (item.isLibraryHub) {
                                window.dispatchEvent(new CustomEvent('toggle-library-hub'));
                              } else if (item.path) {
                                navigate(item.path);
                              } else {
                                goToTab(item.id);
                              }
                              setMobileMenuOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 px-3 py-1.5 rounded-md transition-colors text-xs ${
                              !item.path && !item.isLibraryHub && page === 'mydesk' && activeTab === item.id
                                ? darkMode
                                  ? 'bg-[#70170F]/20 text-[#BC5D41]'
                                  : 'bg-[#BC5D41]/10 text-[#BC5D41]'
                                : darkMode
                                  ? 'text-[#8A8A8A] hover:bg-[#252525] hover:text-[#E5E2DD]'
                                  : 'text-muted-foreground hover:bg-gray-50 hover:text-foreground'
                            } ${sidebarCollapsed ? 'justify-center' : ''}`}
                            data-testid={`nav-${item.id}`}
                          >
                            {renderNavIcon(item, iconAssignments, API, darkMode, !item.path && !item.isLibraryHub && page === 'mydesk' && activeTab === item.id, globalIconSettings)}
                            {!sidebarCollapsed && (
                              <span className="text-sm flex-1 text-left">{item.label}</span>
                            )}
                            {/* Needs-completing badge on the Contacts nav row.
                                Driven by the auto-clearing needs_completion
                                flag — once a contact's company/last_name/
                                group_id gets filled in, the backend recomputes
                                the flag and this count drops naturally. */}
                            {item.id === 'contacts' && needsCompletionCount > 0 && !sidebarCollapsed && (
                              <span
                                className="ml-auto inline-flex items-center justify-center px-1.5 min-w-[18px] h-[18px] rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold border border-amber-300"
                                title={`${needsCompletionCount} contact${needsCompletionCount === 1 ? '' : 's'} need completing`}
                                data-testid="nav-contacts-needs-completion-badge"
                              >
                                {needsCompletionCount > 99 ? '99+' : needsCompletionCount}
                              </span>
                            )}
                          </button>
                        </TooltipTrigger>
                        {sidebarCollapsed && (
                          <TooltipContent side="right" className={darkMode ? 'bg-[#1A1A1A] text-[#E5E2DD] border-[#2A2A2A]' : ''}>
                            {item.label}
                            {item.id === 'contacts' && needsCompletionCount > 0 && (
                              <span className="ml-2 text-amber-600">· {needsCompletionCount} needs completing</span>
                            )}
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  ))}
                </div>
              )}
            </div>
          )})}
          </div>
          
          {/* Collapse Toggle - In sidebar column */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`w-full flex items-center gap-3 px-3 py-2 mb-2 rounded-lg transition-all duration-200 ${
              sidebarCollapsed ? 'justify-center' : ''
            } ${
              darkMode 
                ? 'text-gray-400 hover:bg-white/5 hover:text-gray-200' 
                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
            }`}
            title={sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
            data-testid="sidebar-collapse-toggle"
          >
            {sidebarCollapsed ? (
              <PanelLeft className="w-5 h-5 shrink-0" />
            ) : (
              <>
                <PanelLeftClose className="w-5 h-5 shrink-0" />
                <span className="text-sm font-medium">Collapse</span>
              </>
            )}
          </button>
          </>
          )}
          
          {/* DP57 — the Admin Tools block became one square at the foot of the
              rail, directly above the user avatar. See
              components/layout/AdminToolsSquare.js. All five destinations and
              the build fingerprint moved with it; nothing was dropped. */}
          
        </nav>

        
      {/* DP60 Part 0c/0d/0h — THE FIXED FOOT, and it is IN FLOW rather than
          `fixed`. As a fixed overlay it sat ON TOP of the rail's rows in the
          expanded state — measured, and visible in the screenshots. In flow at
          the end of the rail's flex column it is always visible, the scrolling
          region shrinks to make room, and nothing overlaps anything.
          mb-[64px] reserves the band the avatar occupies (bottom-10) plus the
          space that sets it apart: the avatar is FixedBottomNav's, mounted
          globally, so it stays where it is and the foot stops 16px clear above
          it — LAST, and slightly apart, against the 8px between the boxes. 52px
          left only a 4px gap, measured.
          AI Chad is untouched; the foot positions clear of him. */}
      <div className="km-railfoot shrink-0 mb-[64px]" data-testid="rail-fixed-foot">
        <RailBox
          id="darkmode"
          label={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          mode="icons"
          darkMode={darkMode}
          testId="rail-dark-mode-toggle"
          icon={darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          onClick={toggleDarkMode}
        />
        <AdminToolsSquare
          inline
          darkMode={darkMode}
          isAdmin={isAdmin}
          isFinancial={isFinancial}
          isSuperAdmin={isSuperAdmin}
          iconAssignments={iconAssignments}
          globalIconSettings={globalIconSettings}
        />
      </div>

        {/* Version Info - Bottom of sidebar */}
        <div className={`px-3 py-2 text-xs ${darkMode ? 'text-gray-600' : 'text-gray-400'} ${sidebarCollapsed ? 'text-center' : ''}`}>
          {sidebarCollapsed ? `v${APP_VERSION.split('.').slice(0,2).join('.')}` : `KEY-MADE v${APP_VERSION}`}
        </div>
      </aside>

      <main className="km-shell__main flex-1 flex flex-col min-w-0 overflow-hidden">
        <header
          className="km-topstrip km-topstrip--wrap border-b px-3 sm:px-6 py-2 sm:py-3 flex items-center justify-between overflow-x-hidden"
          data-km-surface="dark"
          data-testid="top-strip"
        >
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              className={`md:hidden h-9 w-9 p-0 ${darkMode ? 'text-[#E5E2DD]' : ''}`}
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>

            <nav
              className={`flex items-center gap-1.5 text-base sm:text-lg font-heading min-w-0 ${darkMode ? 'text-[#E5E2DD]' : ''}`}
              data-testid="shell-breadcrumb"
            >
              {crumbs.map((crumb, i) => (
                <React.Fragment key={crumb}>
                  {i > 0 && <span className="opacity-40 flex-shrink-0">/</span>}
                  {i < crumbs.length - 1 ? (
                    <button
                      onClick={() => navigate(CRUMB_ROUTES[crumb] || '/dashboard')}
                      className="hover:underline flex-shrink-0 opacity-70"
                      data-testid={`shell-breadcrumb-link-${i}`}
                    >
                      {crumb}
                    </button>
                  ) : (
                    <span className="truncate" data-testid="shell-breadcrumb-current">{crumb}</span>
                  )}
                </React.Fragment>
              ))}
            </nav>

            <span className={`hidden sm:flex flex-col border-l pl-3 mr-4 ${darkMode ? 'border-[#2A2A2A]' : 'border-gray-300'}`}>
              <BrandLockup onDark={darkMode} testId="topstrip-brand" />
              <span className="kmd-appversion">v{APP_VERSION}</span>
            </span>
          </div>

          {/* DP82 Part 10a — at 390 this row ran off the screen and took New
              Project, notifications and action items with it: measured at
              x 426, 506 and 546 in a 390 viewport, with the row reporting
              scrollWidth == clientWidth, so nothing scrolled and three
              controls were unreachable. `flex-nowrap` + `max-w-full` makes the
              row itself the scroller, which is the pattern DP79 established
              for the Work Table header and which Chad ruled stays. */}
          <div className="km-topstrip__acts flex flex-nowrap items-center gap-1 sm:gap-3 overflow-x-auto flex-shrink min-w-0 max-w-full scrollbar-hide pb-1 -mb-1">
            {/* PAGE actions — each page supplies its own. */}
            {topStripActions}

            {/* DP60 Part 1c — presence and notifications are GLOBAL. They were
                My Desk's alone; every page in the shell gets the same two now,
                from one place. Moved, not rebuilt. */}
            <div className="hidden sm:block" data-testid="shell-whos-online">
              <WhosOnline darkMode={darkMode} compact />
            </div>
            <button type="button" className="sm:hidden km-shell__chad" onClick={() => window.dispatchEvent(new CustomEvent('open-ai-chad'))} aria-label="Ask AI Chad" data-testid="shell-ai-chad">
              <span>AI</span>
            </button>
            <div data-testid="shell-notifications">
              <NotificationsPanel darkMode={darkMode} userId={getCurrentUserId()} userRole={role} />
            </div>
            {/* DP81 Part 1c — GlobalActionItems is PORTFOLIO-WIDE, not page
                specific: it reads GET /action-items across the tenant. It lived
                on one page out of 110 (pages/Dashboard.js) inside that page's
                own duplicate header. It joins the group DP60 established, so
                every page in the shell reaches it from one place.
                Moved, not rebuilt. */}
            <div data-testid="shell-action-items">
              <GlobalActionItems />
            </div>
          </div>
        </header>

        {belowTopStrip}

        <div
          className="km-shell__content flex-1"
          data-testid="shell-content"
          ref={contentRef}
        >
          {/* DP94 Part 1 — the MASTHEAD SLOT. A slot on AppShell rather than three
              pages each rendering above {children}: the band has to be a sibling of
              .km-panel, not a child, and one place to change beats three. It is
              always in the DOM so components/layout/ShellMasthead.js can portal a
              gateway's own band into it; empty, it collapses and nothing shows.
              Pages that put nothing here keep their header inside the panel, mat on
              both sides.
              DP95 Part 1 — and it SHRINKS as the panel scrolls, down to a 48px bar
              that carries the page's name and opens the band again when clicked. */}
          <div id="km-shell-mast" className="km-shell__mast" data-testid="shell-masthead"
               ref={mastRef}>
            {masthead}
            <button
              type="button"
              className="km-shell__mastbar"
              onClick={openBand}
              data-testid="masthead-collapsed-bar"
              aria-label={`Open the ${crumbs[crumbs.length - 1]} masthead`}
            >
              <span className="km-shell__mastbar-name">{crumbs[crumbs.length - 1]}</span>
              <ChevronDown className="w-3 h-3" aria-hidden="true" />
            </button>
          </div>
          {/* DP93 Part 1c — the PANEL, inside the mat. The content region is the
              mat now; this is the print. It is also the only scroller, so the
              scrollbar lands at the panel's edge with the mat beside it. */}
          {/* tabIndex so the keyboard can scroll it — DP95, after the collapsed
              bar took focus on click and PageDown then did nothing */}
          <div className="km-panel" data-testid="shell-panel" ref={panelRef} tabIndex={-1}>
            {children}
          </div>
          {/* SH-1 — the DOCK: every right-side panel portals in here, inside the
              frame, so the top strip and the page's own edge stay uncovered. */}
          <div id="km-dock" className="km-dock" data-testid="km-dock" />
        </div>
      </main>
    </div>
    </div>
  );
};

export default AppShell;
